import {
  PdfGen,
  PageGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfColor,
  PdfFont,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { OptsImage } from '../optsImage';
import { IItemCount } from '@educraft/base';
import { GUtil } from '../../pdf/gutil';
import { ESymbol } from '../../pdf/draw/symbol';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';

interface Key {
  key: string;
  u: number;
  special: boolean;
  two: boolean;
  symbol?: ESymbol;
}

function K(
  key: string,
  u = 1.0,
  special = false,
  two = false,
  symbol?: ESymbol
): Key {
  return { key, u, special, two, symbol };
}

/**
 * Klavesnice (Keyboard) - Keyboard layout with images on letter keys
 *
 * Layout: 2 landscape pages.
 * Page 1: Czech keyboard layout. Page 2: US keyboard layout.
 * Letter keys show images whose labels start with that letter.
 *
 * Migrated from C# EduFrame.Img10.Generators.Klavesnice
 */
export class Klavesnice implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 10;
  }
  countMax(): number {
    return 10;
  }

  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const cz: Key[][] = [
      [
        K(';°', 1, false, true),
        K('+1', 1, false, true),
        K('ě2', 1, false, true),
        K('š3', 1, false, true),
        K('č4', 1, false, true),
        K('ř5', 1, false, true),
        K('ž6', 1, false, true),
        K('ý7', 1, false, true),
        K('á8', 1, false, true),
        K('í9', 1, false, true),
        K('é0', 1, false, true),
        K('=%', 1, false, true),
        K('´ˇ', 1, false, true),
        K('Backspace', 1.5, true),
      ],
      [
        K('Tab', 1.5, true),
        K('Q'),
        K('W'),
        K('E'),
        K('R'),
        K('T'),
        K('Z'),
        K('U'),
        K('I'),
        K('O'),
        K('P'),
        K('ú/', 1, false, true),
        K(')(', 1, false, true),
        K("¨'", 1, false, true),
      ],
      [
        K('Caps Lock', 1.75, true),
        K('A'),
        K('S'),
        K('D'),
        K('F'),
        K('G'),
        K('H'),
        K('J'),
        K('K'),
        K('L'),
        K('ů"', 1, false, true),
        K('§!', 1, false, true),
        K('Enter', 1.75, true),
      ],
      [
        K('Shift', 2.0, true),
        K('Y'),
        K('X'),
        K('C'),
        K('V'),
        K('B'),
        K('N'),
        K('M'),
        K(',?', 1, false, true),
        K('.:', 1, false, true),
        K('-_', 1, false, true),
        K('Shift', 2.5, true),
      ],
      [
        K('Ctrl', 1.25, true),
        K('Win', 1.25, true),
        K('Alt', 1.25, true),
        K('Space', 5.75, true),
        K('Alt', 1.25, true),
        K('Win', 1.25, true),
        K('', 1.25, true, false, ESymbol.KeyMenu),
        K('Ctrl', 1.25, true),
      ],
    ];

    const us: Key[][] = [
      [
        K('`~', 1, false, true),
        K('1!', 1, false, true),
        K('2@', 1, false, true),
        K('3#', 1, false, true),
        K('4$', 1, false, true),
        K('5%', 1, false, true),
        K('6^', 1, false, true),
        K('7&', 1, false, true),
        K('8*', 1, false, true),
        K('9(', 1, false, true),
        K('0)', 1, false, true),
        K('-_', 1, false, true),
        K('=+', 1, false, true),
        K('Backspace', 1.5, true),
      ],
      [
        K('Tab', 1.5, true),
        K('Q'),
        K('W'),
        K('E'),
        K('R'),
        K('T'),
        K('Y'),
        K('U'),
        K('I'),
        K('O'),
        K('P'),
        K('[{', 1, false, true),
        K('}]', 1, false, true),
        K('\\|', 1, false, true),
      ],
      [
        K('Caps Lock', 1.75, true),
        K('A'),
        K('S'),
        K('D'),
        K('F'),
        K('G'),
        K('H'),
        K('J'),
        K('K'),
        K('L'),
        K(';:', 1, false, true),
        K('\'"', 1, false, true),
        K('Enter', 1.75, true),
      ],
      [
        K('Shift', 2.0, true),
        K('Z'),
        K('X'),
        K('C'),
        K('V'),
        K('B'),
        K('N'),
        K('M'),
        K(',<', 1, false, true),
        K('.>', 1, false, true),
        K('/?', 1, false, true),
        K('Shift', 2.5, true),
      ],
      [
        K('Ctrl', 1.25, true),
        K('Win', 1.25, true),
        K('Alt', 1.25, true),
        K('Space', 5.75, true),
        K('Alt', 1.25, true),
        K('Win', 1.25, true),
        K('', 1.25, true, false, ESymbol.KeyMenu),
        K('Ctrl', 1.25, true),
      ],
    ];

    const page1 = pdf.addPage(true);
    const page2 = pdf.addPage(true);

    this.drawKeyboard(page1, opts, cz);
    this.drawKeyboard(page2, opts, us);
  }

  private drawKeyboard(page: PageGen, opts: OptsImage, keys: Key[][]): void {
    const margin = PdfUnit.FromMillimeters(5);
    const sm = new PdfFont(PdfFont.FONT_NAME, 16);
    const background = new PdfBrush(PdfColor.fromCmyk(0, 0, 0, 0.1));
    const round = PdfUnit.FromMillimeters(3);

    const pageW = PdfSize.A4.width;
    const pageH = PdfSize.A4.height;

    const max = Math.max(...keys.map((row) => row.reduce((sum, k) => sum + k.u, 0)));
    const sz = (pageH - 2 * margin) / max;
    const originX = margin;
    const originY = (pageW - keys.length * sz) / 2;

    for (let line = 0; line < keys.length; line++) {
      let offset = 0;
      for (let k = 0; k < keys[line].length; k++) {
        const key = keys[line][k];
        const rect = new PdfRect(originX + offset, originY + line * sz, sz * key.u, sz);
        const r1 = new PdfRect(rect.x, rect.y, rect.width, rect.height / 2);
        const r2 = new PdfRect(rect.x, rect.y + rect.height / 2, rect.width, rect.height / 2);

        if (key.symbol) {
          page.drawSymbol(key.symbol, PdfBrush.Black, rect);
        } else if (key.special) {
          page.drawString(key.key, sm, PdfBrush.Black, null, rect, ETextAlign.Center);
        } else if (key.two) {
          page.drawString(key.key.substring(1, 2), sm, PdfBrush.Black, null, r1, ETextAlign.Center);
          page.drawString(key.key.substring(0, 1), sm, PdfBrush.Black, null, r2, ETextAlign.Center);
        } else {
          const lbl = opts.list.find(
            (item) => item.label?.substring(0, 1).toUpperCase() === key.key
          );
          page.drawRoundedRectangle(null, background, rect, round);

          if (lbl) {
            page.drawImageCenter(lbl.image, rect);
          } else {
            page.drawString(key.key, GUtil.baseFont, PdfBrush.Black, null, rect, ETextAlign.Center);
          }
        }

        page.drawRoundedRectangle(GUtil.basePen, null, rect, round);
        offset += sz * key.u;
      }
    }
  }
}
